import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";

import { type Rotation, rotatePoint } from "./rotationUtils";

export type CommentBbox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

const MIN_ZOOM = 0.5;
/** Manual toolbar zoom can go to 10x; auto-focus stays well below that so the
 * full bbox (plus margin) stays visible in the viewport. */
// why: 7x put reviewers at 700% on a one-word finding with no surrounding artwork to judge it against (Ask V2 story test, P1-8)
const MAX_AUTO_FOCUS_ZOOM = 2;
const BBOX_FIT_PADDING_RATIO = 0.22;
/** Tiny LLM bboxes (single words) otherwise drive extreme zoom-in. Floor the
 * effective size used for fit math so focus stays contextual, not microscopic.
 * Sized so the floor alone yields exactly MAX_AUTO_FOCUS_ZOOM at the default
 * padding (`(1 - 2*0.22) / 0.28 = 2`). */
const MIN_BBOX_W_FOR_ZOOM = 0.28;
const MIN_BBOX_H_FOR_ZOOM = 0.28;

/** Transform a page-relative bbox into displayed (post-rotation) space. */
export function computeDisplayedBbox(
  bbox: CommentBbox,
  rotation: Rotation,
): CommentBbox {
  const corners = [
    rotatePoint({ x: bbox.x, y: bbox.y }, rotation),
    rotatePoint({ x: bbox.x + bbox.w, y: bbox.y }, rotation),
    rotatePoint({ x: bbox.x, y: bbox.y + bbox.h }, rotation),
    rotatePoint({ x: bbox.x + bbox.w, y: bbox.y + bbox.h }, rotation),
  ];
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    w: Math.max(...xs) - x,
    h: Math.max(...ys) - y,
  };
}

/** Zoom level that fits a displayed bbox in the viewport with generous padding.
 * Capped below manual max zoom so the spotlight hole stays fully on screen. */
export function computeZoomToFitBbox(
  displayedBbox: CommentBbox,
  paddingRatio = BBOX_FIT_PADDING_RATIO,
): number {
  const pad = paddingRatio * 2;
  const w = Math.max(displayedBbox.w, MIN_BBOX_W_FOR_ZOOM);
  const h = Math.max(displayedBbox.h, MIN_BBOX_H_FOR_ZOOM);
  const fitW = w > 0 ? (1 - pad) / w : MAX_AUTO_FOCUS_ZOOM;
  const fitH = h > 0 ? (1 - pad) / h : MAX_AUTO_FOCUS_ZOOM;
  const target = Math.min(fitW, fitH);
  return Math.max(MIN_ZOOM, Math.min(MAX_AUTO_FOCUS_ZOOM, target));
}

export function getActiveCommentDisplayedBbox(
  comments: BaseTicketComment[] | undefined,
  activeCommentId: string | null | undefined,
  pageNum: number,
  rotation: Rotation,
): CommentBbox | null {
  if (!activeCommentId) return null;
  const comment = (comments ?? []).find((c) => c._id === activeCommentId);
  if (!comment?.bbox) return null;
  if ((comment.page_num ?? 1) !== pageNum) return null;
  return computeDisplayedBbox(comment.bbox, rotation);
}
